import React from 'react';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAIModel } from '../contexts/AIModelContext';
import { Sparkles, Brain, Cpu } from 'lucide-react';

const PROVIDER_ICON = {
  openai: <Cpu className="h-3.5 w-3.5 mr-2 text-emerald-500" />,
  anthropic: <Brain className="h-3.5 w-3.5 mr-2 text-purple-500" />,
  gemini: <Sparkles className="h-3.5 w-3.5 mr-2 text-blue-500" />,
};

// Reusable, provider-grouped AI model selector bound to the global AIModelContext.
// Only enabled + available models are selectable (unavailable ones are shown disabled).
const ModelSelector = ({ className = 'w-48', triggerTestId = 'ai-model-select' }) => {
  const { model, setModel, grouped, models, canSelect } = useAIModel();
  if (!canSelect) return null;

  // Keep the trigger label sensible even for the legacy "gemini" alias
  const displayValue = models.find((m) => m.id === model)?.display_name
    || (model === 'gemini' ? 'Gemini 2.5 Flash' : model);

  return (
    <Select value={model} onValueChange={setModel}>
      <SelectTrigger className={className} data-testid={triggerTestId}>
        <SelectValue placeholder="Select AI model">{displayValue}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {Object.entries(grouped).map(([label, list]) => (
          list && list.length ? (
            <SelectGroup key={label}>
              <SelectLabel>{label}</SelectLabel>
              {list.filter((m) => m.enabled).map((m) => (
                <SelectItem
                  key={m.id}
                  value={m.id}
                  disabled={!m.available}
                  data-testid={`ai-model-option-${m.id}`}
                >
                  <span className="flex items-center">
                    {PROVIDER_ICON[m.provider]}
                    {m.display_name}
                    {!m.available && <span className="ml-2 text-xs text-slate-400">(unavailable)</span>}
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null
        ))}
      </SelectContent>
    </Select>
  );
};

export default ModelSelector;
